/**
 * Single source of truth for the initial-onboarding sidebar lock.
 *
 * The lock hard-collapses the sidebar so a brand-new account cannot leave the onboarding chat. Two
 * places must agree on exactly when it is active: the sidebar itself, and the deep-link resolver
 * (`/dl/...`) — which parks the intent and falls back to onboarding while locked, and resolves the
 * link once unlocked. They previously computed it separately and drifted: the resolver keyed on the
 * strict per-brand `brands[0].onboardingCompleted`, while the sidebar keyed on this global signal —
 * so a fully-functional user (unlocked sidebar) whose current brand was backfilled or never resolved
 * the post-onboarding modal had EVERY deep link silently sent to the latest-task fallback. Sharing
 * this one function is what stops that drift from ever recurring.
 *
 * Locked ⟺ brands have loaded AND no brand finished onboarding end-to-end AND the account is still
 * in the initial flow (no real brand yet, or the first brand's live onboarding session is active).
 */
export function computeIsOnboardingSidebarLocked(params: {
    /** The brands query has resolved. Never lock before it does, or every cold load would collapse. */
    brandsLoaded: boolean
    /** Some brand finished onboarding end-to-end (`onboardingCompleted`). */
    hasCompletedOnboardingBrand: boolean
    /** The account has no real (non-empty) brand yet. */
    isUserInOnboarding: boolean
    /** The account has at most one brand. */
    isFirstBrand: boolean
    /** The first brand's live onboarding session is still running (in-memory flag). */
    isOnboardingFlowActive: boolean
}): boolean {
    const { brandsLoaded, hasCompletedOnboardingBrand, isUserInOnboarding, isFirstBrand, isOnboardingFlowActive } =
        params

    return (
        brandsLoaded &&
        !hasCompletedOnboardingBrand &&
        (isUserInOnboarding || (isFirstBrand && isOnboardingFlowActive))
    )
}
